/**
 * Migration utility to move characters from legacy localStorage (mythkit_characters) to userDataStore.
 * Called after user logs in for the first time (or on app load if we detect legacy data).
 */

import { entityApi } from '@/api/entityClient';
import * as userDataStore from './userDataStore.js';

const LEGACY_STORAGE_KEY = 'mythkit_characters';

function normName(name: unknown): string {
  return String(name ?? '')
    .trim()
    .toLowerCase();
}

export async function migrateLocalStorageCharacters(): Promise<{
  migrated: number;
  failed: number;
}> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LEGACY_STORAGE_KEY) : null;
    const localCharacters = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(localCharacters) || localCharacters.length === 0) {
      return { migrated: 0, failed: 0 };
    }

    const existing = await userDataStore.getAllCharacters();
    const existingByName = new Set(
      (existing || []).map((c) => normName(c?.name)).filter((n) => n.length > 0)
    );
    const existingServerIds = new Set(
      (existing || [])
        .map((c) => (c?.server_id != null ? Number(c.server_id) : NaN))
        .filter((n) => Number.isFinite(n))
    );

    let migrated = 0;
    let failed = 0;

    for (const character of localCharacters) {
      const sidRaw = character?.server_id ?? character?.id;
      const sid = sidRaw != null ? Number(sidRaw) : NaN;
      if (Number.isFinite(sid) && existingServerIds.has(sid)) {
        continue;
      }
      const n = normName(character?.name);
      if (n && existingByName.has(n)) {
        continue;
      }
      try {
        await entityApi.entities.Character.create({
          ...character,
          name: character.name || 'Unnamed',
          level: character.level || 1,
        });
        migrated++;
        if (n) existingByName.add(n);
        if (Number.isFinite(sid)) existingServerIds.add(sid);
      } catch (err) {
        failed++;
      }
    }

    if (failed === 0 && typeof localStorage !== 'undefined') {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }

    return { migrated, failed };
  } catch (error) {
    return { migrated: 0, failed: 0 };
  }
}
